import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input } from '@mlm/ui';
import { searchAdminUsers, type AdminUserSearchResult } from '../lib/api';

/**
 * Active-member autocomplete picker for admin features that need to select a
 * single user (Default Sponsor today; reusable for slot ownership / branch
 * owner reassignment / etc). Debounced 250ms, server-side ILIKE on
 * name/email/referral_code, capped at 10 results.
 *
 * Controlled: parent owns the selected user. Pass `selected` (the resolved
 * row or null) + `onChange` (called with the picked user or null on clear).
 */
export function MemberPicker({
  selected,
  onChange,
  disabled,
  placeholder = 'Search by name, email, or referral code…',
}: {
  selected: AdminUserSearchResult | null;
  onChange: (user: AdminUserSearchResult | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const searchQ = useQuery({
    queryKey: ['admin-user-search', debounced],
    queryFn: () => searchAdminUsers(debounced),
    enabled: open && debounced.length > 0,
    staleTime: 30_000,
  });

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 p-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{selected.fullName ?? '(no name)'}</p>
          <p className="truncate text-xs text-muted-foreground">
            {selected.email}
            {selected.referralCode ? ` · ${selected.referralCode}` : ''}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          Clear
        </Button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
      />
      {open && debounced.length > 0 && (
        <div
          id={listId}
          role="listbox"
          className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover shadow-md"
        >
          {searchQ.isLoading ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
          ) : !searchQ.data || searchQ.data.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">No matches.</p>
          ) : (
            <ul className="divide-y">
              {searchQ.data.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    role="option"
                    className="block w-full px-3 py-2 text-left hover:bg-muted"
                    onClick={() => {
                      onChange(u);
                      setQuery('');
                      setOpen(false);
                    }}
                  >
                    <p className="truncate text-sm font-medium">{u.fullName ?? '(no name)'}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {u.email}
                      {u.referralCode ? ` · ${u.referralCode}` : ''}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
